import {APP_INITIALIZER, NgModule} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import {BrowserModule} from '@angular/platform-browser';
import {provideAnimationsAsync} from '@angular/platform-browser/animations/async';
import {providePrimeNG} from 'primeng/config';
// PrimeNG 22: themes moved to @primeuix/themes; the -compat preset is calibrated for the
// Serenity layout's 14px root font-size (plain Aura assumes 16px and would shrink everything).
import {definePreset} from '@primeuix/themes';
import Aura from '@primeuix/themes/aura-compat';
import {HashLocationStrategy, LocationStrategy} from '@angular/common';
import {environment} from '../environments/environment';
import {PRIMEUI_LICENSE_KEY} from '../environments/license';
import {AppRoutingModule} from './app-routing.module';

import {AppComponent} from './app.component';
import {AppMainComponent} from './app.main.component';
import {AppConfigComponent} from './app.config.component';
import {AppNotfoundComponent} from './core/pages/app.notfound.component';
import {AppErrorComponent} from './core/pages/app.error.component';
import {AppAccessdeniedComponent} from './core/pages/app.accessdenied.component';
import {AppMenuComponent} from './app.menu.component';
import {AppMenuitemComponent} from './app.menuitem.component';
import {AppBreadcrumbComponent} from './app.breadcrumb.component';
import {AppTopBarComponent} from './app.topbar.component';
import {AppFooterComponent} from './app.footer.component';

import {ConfigService} from './app.config.service';
import {AppBreadcrumbService} from './app.breadcrumb.service';
import {MenuService} from './app.menu.service';

import {IndexGuard} from "./core/guards";
import {AuthService} from "./core/service/auth.service";
import {TokenInterceptor} from "./core/service/token.interceptor";
import {SearchService} from "./core/service/search.service";
import {CoreModule} from "./core/core.module";
import {MarketService} from "./core/service/market.service";

import {provideTranslateService, TranslatePipe, TranslateDirective} from "@ngx-translate/core";
import {provideTranslateHttpLoader} from "@ngx-translate/http-loader";
import {GravatarModule} from 'ngx-gravatar';
import {ButtonModule} from 'primeng/button';
import {ToggleSwitchModule} from "primeng/toggleswitch";
import {MessageModule} from 'primeng/message';
import {RadioButtonModule} from "primeng/radiobutton";
import {ScrollPanelModule} from 'primeng/scrollpanel';
import {TabsModule} from 'primeng/tabs';
import {ToastModule} from 'primeng/toast';
import {MessageService} from "primeng/api";
import {TooltipModule} from "primeng/tooltip";
import {DialogModule} from "primeng/dialog";
import {PasswordModule} from "primeng/password";
import {FloatLabelModule} from "primeng/floatlabel";

// Aura preset with the deep-purple brand palette (Material Deep Purple ramp).
const AuraDeepPurple = definePreset(Aura, {
    semantic: {
        primary: {
            50: '#ede7f6', 100: '#d1c4e9', 200: '#b39ddb', 300: '#9575cd', 400: '#7e57c2',
            500: '#673ab7', 600: '#5e35b1', 700: '#512da8', 800: '#4527a0', 900: '#311b92', 950: '#21005d'
        }
    }
});

export function authServiceFactory(authService: AuthService): Function {
    return () => authService.reloadUserFromServer();
}

export function loadMarkets(marketService: MarketService): Function {
    return () => marketService.markets;
}

@NgModule({ declarations: [
        AppComponent,
        AppMainComponent,
        AppMenuComponent,
        AppMenuitemComponent,
        AppConfigComponent,
        AppBreadcrumbComponent,
        AppTopBarComponent,
        AppFooterComponent,
        AppNotfoundComponent,
        AppErrorComponent,
        AppAccessdeniedComponent
    ],
    bootstrap: [AppComponent], imports: [AppRoutingModule,
        CoreModule,
        BrowserModule,
        FormsModule,
        ReactiveFormsModule,
        TranslatePipe,
        TranslateDirective,
        GravatarModule.forRoot({}),
        ButtonModule,
        ToggleSwitchModule,
        MessageModule,
        RadioButtonModule,
        ScrollPanelModule,
        TabsModule,
        ToastModule,
        TooltipModule,
        DialogModule,
        PasswordModule,
        FloatLabelModule], providers: [
        { provide: LocationStrategy, useClass: HashLocationStrategy },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TokenInterceptor,
            multi: true
        },
        MenuService, AppBreadcrumbService, ConfigService,
        IndexGuard, AuthService, SearchService, MarketService, MessageService,
        {
            provide: APP_INITIALIZER,
            useFactory: authServiceFactory,
            deps: [AuthService],
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: loadMarkets,
            deps: [MarketService],
            multi: true
        },
        provideHttpClient(withXhr(), withInterceptorsFromDi()),
        provideTranslateService({
            loader: provideTranslateHttpLoader({prefix: './assets/i18n/', suffix: '.json'})
        }),
        provideAnimationsAsync(),
        providePrimeNG({
            ripple: true,
            theme: {preset: AuraDeepPurple, options: {darkModeSelector: '.dark'}},
            // PrimeUI licencia (PrimeNG 22+); prázdny kľúč = licenčný banner.
            // Kľúč je tajný - negeneruje sa do gitu, pozri tools/gen-license.mjs.
            license: PRIMEUI_LICENSE_KEY,
        }),
    ] })
export class AppModule {
}
